import { useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { IconButton, Tooltip, Badge, CircularProgress } from '@mui/material';
import { useCobrowse } from '../../hooks/useCobrowse';
import SupportSessionIcon from './SupportSessionIcon';
import { supportSessionLabel } from './supportSession';


/**
 * Toolbar button that starts a cobrowse support session, and while one runs,
 * brings its chat back. The session code is in the label, so the user can read
 * it to support.
 */
export default function CobrowseButton() {
  const auth = useAuth();
  const getAccessToken = useCallback(async () => {
    return auth.user?.access_token ?? null;
  }, [auth.user?.access_token]);

  const { status, sessionCode, agentCount, error, startSession, openChat } = useCobrowse(getAccessToken);
  const isActive = status === 'active';
  const isLoading = status === 'connecting' || status === 'requesting-consent';

  const handleClick = () => {
    if (isActive) {
      // Brings the chat back; ending is in the chat. See supportSessionLabel.
      openChat();
    } else if (!isLoading) {
      startSession();
    }
  };

  // The same words as the status strip's button, so the two read as one action.
  const label = supportSessionLabel(status, sessionCode);

  return (
    <>
      <Tooltip title={label}>
        <span>
          <IconButton
            onClick={handleClick}
            disabled={isLoading}
            color={isActive ? 'success' : 'default'}
            size="small"
            // Named explicitly because the Tooltip cannot do it here: MUI puts
            // the name on its DIRECT child, and that is the <span> wrapper —
            // which is itself required, since a disabled button does not emit
            // the events Tooltip listens for. So the tooltip reads fine with a
            // mouse while the button announces as just "button".
            aria-label={label}
            className="support-session-btn"
          >
            {isLoading ? (
              <CircularProgress size={20} />
            ) : (
              <Badge
                badgeContent={agentCount > 0 ? agentCount : undefined}
                color="info"
                variant="standard"
              >
                <SupportSessionIcon active={isActive} />
              </Badge>
            )}
          </IconButton>
        </span>
      </Tooltip>

      {/* Error snackbar handled by existing alert system - just log */}
      {error && <CobrowseError error={error} />}
    </>
  );
}

function CobrowseError({ error }: { error: string }) {
  // Log to console — the existing NotificationProvider handles toasts
  console.error('[Cobrowse]', error);
  return null;
}
